/**
 * persona-mapping.ts - SPEC-web-planner-007 AC-17 / AC-30 (C5).
 *
 * Mirrors the backend BuildingBlocks `PersonaMapping` table EXACTLY. Roles are
 * matched case, space, '-' and '_' insensitively. The shared fixture
 * `persona-mapping.fixture.json` (next to this file) is a byte-for-byte copy of
 * `awvent-backend/tests/Awvent.Platform.Tests/Fixtures/persona-mapping.json`.
 * The two copies MUST stay identical (AC-30); persona-mapping.spec.ts runs every
 * row through this file.
 *
 * Membership of workspace W: the user's tenants hold an entry with id == W whose
 * invitationStatus is not 'Pending' (null/empty counts as accepted) and whose
 * persona is not none.
 */

export type PersonaKind = 'admin' | 'cro' | 'fs' | 'is';
/** `null` means "none" (deny). */
export type Persona = PersonaKind | null;

/** Minimal structural shapes so any UserDto / TenantCompactDto fits. */
export interface PersonaTenantEntry {
    id?: string | null;
    invitationStatus?: string | null;
    roles?: ReadonlyArray<string | null | undefined> | null;
}
export interface PersonaUser {
    tenants?: ReadonlyArray<PersonaTenantEntry> | null;
}

const normalise = (role: string | null | undefined): string =>
    String(role ?? '').toLowerCase().replace(/[\s\-_]/g, '');

const ROLE_TO_PERSONA: Readonly<Record<string, PersonaKind>> = {
    owner: 'admin',
    admin: 'admin',
    administrator: 'admin',
    superadmin: 'admin',
    is: 'is',
    insidesales: 'is',
    fs: 'fs',
    fieldsales: 'fs',
    cro: 'cro',
    chiefrevenueofficer: 'cro',
    // legacy (D6)
    teammember: 'is',
    user: 'is',
};

// Highest first: admin > cro > fs > is.
const PRIORITY: ReadonlyArray<PersonaKind> = ['admin', 'cro', 'fs', 'is'];

/** Persona for a single role string; null = none. */
export function personaForRole(role: string | null | undefined): Persona {
    return ROLE_TO_PERSONA[normalise(role)] ?? null;
}

/** Highest persona across several roles; null when none map. */
export function personaForRoles(roles: ReadonlyArray<string | null | undefined> | null | undefined): Persona {
    const found = new Set<PersonaKind>();
    for (const r of roles ?? []) {
        const p = personaForRole(r);
        if (p) {
            found.add(p);
        }
    }
    return PRIORITY.find((p) => found.has(p)) ?? null;
}

/** True when the role list contains SuperAdmin (normalised). */
export function rolesHaveSuperAdmin(roles: ReadonlyArray<string | null | undefined> | null | undefined): boolean {
    return (roles ?? []).some((r) => normalise(r) === 'superadmin');
}

const isPending = (e: PersonaTenantEntry): boolean =>
    String(e.invitationStatus ?? '').trim().toLowerCase() === 'pending';

/** Persona of `user` in workspace `workspaceId`, or null when not a member. */
export function personaFor(user: PersonaUser | null | undefined, workspaceId: string | null | undefined): Persona {
    if (!user || !workspaceId) {
        return null;
    }
    // Backend rule: a member if ANY non-Pending entry for the workspace qualifies;
    // the persona is the highest across all such entries (duplicates are possible).
    const roles = (user.tenants ?? [])
        .filter((t) => !!t && t.id === workspaceId && !isPending(t))
        .flatMap((t) => t.roles ?? []);
    return personaForRoles(roles);
}

/** SuperAdmin role on a non-Pending entry of THIS workspace. */
export function isSuperAdminIn(user: PersonaUser | null | undefined, workspaceId: string | null | undefined): boolean {
    return !!workspaceId && (user?.tenants ?? []).some((t) => !!t && t.id === workspaceId && !isPending(t) && rolesHaveSuperAdmin(t.roles));
}

/** SuperAdmin role on any non-Pending entry (backend IsSuperAdmin). */
export function isSuperAdmin(user: PersonaUser | null | undefined): boolean {
    return (user?.tenants ?? []).some((t) => !!t && !isPending(t) && rolesHaveSuperAdmin(t.roles));
}

/**
 * Landing route per persona. Every value is a route PersonaGuard never denies
 * for that persona, except cro on a plan without CRO (matches AppShellService HOME_ROUTE for admin/cro/fs/is):
 *   admin -> /admin       (PersonaGuard-only; admin allowed)
 *   cro   -> /portfolio   (PersonaGuard 'cro': denied on a plan without CRO, then deniedTarget -> /home;
 *                          the post-login redirect and the app shell send such a cro to /home)
 *   fs    -> /deals       (RoleGuard only; a member passes)
 *   is    -> /home        (RoleGuard only; a member passes)
 *   none  -> /tenant-selection (AuthGuard only, outside every persona-guarded mount)
 * `deals` and `home` carry RoleGuard, which redirects a SuperAdmin member of the
 * workspace to /admin/subscription. So for such a user (superAdminHere) homeFor
 * returns /admin/subscription directly, and a PersonaGuard denial never lands on
 * a RoleGuard redirect: at most one hop.
 */
export function homeFor(persona: Persona, superAdminHere: boolean = false): string {
    if (superAdminHere) {
        return '/admin/subscription';
    }
    switch (persona) {
        case 'admin': return '/admin';
        case 'cro': return '/portfolio';
        case 'fs': return '/deals';
        case 'is': return '/home';
        default: return '/tenant-selection';
    }
}

/**
 * Effective persona. A SuperAdmin counts as an admin member of every workspace
 * (spec Definitions, E8), so an admin persona is granted even without an entry.
 */
export function resolvePersona(user: PersonaUser | null | undefined, workspaceId: string | null | undefined): { persona: Persona; superAdmin: boolean; member: boolean; superAdminHere: boolean } {
    const superAdmin = isSuperAdmin(user);
    const own = personaFor(user, workspaceId);
    const persona = own ?? (superAdmin ? 'admin' : null);
    // member = holds a qualifying entry in this workspace (a SuperAdmin without one is not a member)
    return { persona, superAdmin, member: own !== null, superAdminHere: isSuperAdminIn(user, workspaceId) };
}

